(() => {
  'use strict';

  const BATCH_SIZE  = 12; // over-fetch to absorb show_on_archive_page_only filtered products
  const API_VERSION = '2025-01';
  const ROOT_MARGIN = '0px 0px 800px 0px'; // trigger 400px before sentinel enters view
  const CACHE_TTL   = 60 * 60 * 1000; // 1 hour

  // ─── Bootstrap from DOM ───────────────────────────────────────────────────

  const container = document.getElementById('shop-products');
  if (!container) return;

  const token       = container.dataset.storefrontToken;
  const shopDomain  = container.dataset.shopDomain;
  const collection  = container.dataset.collectionHandle;
  const moneyFormat = container.dataset.moneyFormat; // e.g. "${{amount}}"
  const grid        = document.getElementById('shop-product-grid');
  const sentinel    = document.getElementById('shop-scroll-sentinel');
  const loadingEl   = document.getElementById('shop-loading-indicator');
  const errorEl     = document.getElementById('shop-load-error');
  const retryBtn    = document.getElementById('shop-retry-btn');

  // Sentinel is only rendered when there is a next page AND a token exists.
  if (!sentinel || !token || !grid) return;

  // ─── State ────────────────────────────────────────────────────────────────

  // cursor starts null — on first trigger we either load from cache (which
  // provides a real cursor) or bootstrap via a lightweight query to get the
  // endCursor after the 8 server-rendered products.
  let cursor          = null;
  let hasNext         = container.dataset.hasNext === 'true';
  let isFetching      = false;
  let abortController = null;

  // ─── localStorage Cache ───────────────────────────────────────────────────
  //
  // Stores compact product data so repeat visits skip the Storefront API
  // entirely (or at least resume from the last fetched cursor).
  //
  // Cache shape:
  //   { ts: Number, cursor: String|null, hasNext: Boolean, products: CompactProduct[] }
  //
  // CompactProduct shape:
  //   { title, path, available, price: {amount, currency},
  //     thumbs: [{ color, isDefault, main: ImgData|null, hover: ImgData|null }] }
  //
  // ImgData shape:
  //   { url, alt, w, h }

  const CACHE_KEY = `shop_scroll__${shopDomain}__${collection}`;

  function readCache() {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (Date.now() - data.ts > CACHE_TTL) { localStorage.removeItem(CACHE_KEY); return null; }
      return data;
    } catch {
      return null;
    }
  }

  function appendCache(newProducts, endCursor, nextPage) {
    try {
      const existing = readCache();
      const ts       = existing ? existing.ts : Date.now();
      const products = existing ? [...existing.products, ...newProducts] : newProducts;
      localStorage.setItem(CACHE_KEY, JSON.stringify({ ts, cursor: endCursor, hasNext: nextPage, products }));
    } catch {
      // localStorage full or disabled — silently skip caching
    }
  }

  // ─── GraphQL Queries ──────────────────────────────────────────────────────
  //
  // BOOTSTRAP_QUERY: minimal query to get a valid endCursor after the first 8
  // server-rendered products. No product fields fetched — cursor only.

  const BOOTSTRAP_QUERY = /* graphql */`
    query ShopBootstrap($handle: String!, $first: Int!) {
      collection(handle: $handle) {
        products(first: $first) {
          pageInfo { hasNextPage endCursor }
        }
      }
    }
  `;

  // QUERY: full product data for rendering subsequent batches.
  // Fetches products with:
  //   - images (first 20) with intrinsic dimensions for CLS prevention
  //   - color options to replicate the multi-thumb logic in product-thumb.liquid
  //   - two custom metafields: show_on_archive_page_only, no_hover_image
  //   - onlineStoreUrl for deriving the product path
  //   - priceRange for price display and sold-out detection

  const QUERY = /* graphql */`
    query ShopInfiniteScroll($handle: String!, $first: Int!, $after: String) {
      collection(handle: $handle) {
        products(first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              title
              availableForSale
              onlineStoreUrl
              priceRange {
                minVariantPrice { amount currencyCode }
              }
              options { name values }
              images(first: 20) {
                edges {
                  node {
                    url(transform: { maxWidth: 2000 })
                    altText
                    width
                    height
                  }
                }
              }
              metafields(identifiers: [
                { namespace: "custom", key: "show_on_archive_page_only" }
                { namespace: "custom", key: "no_hover_image" }
              ]) { namespace key value }
            }
          }
        }
      }
    }
  `;

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function getMetafield(metafields, ns, key) {
    const mf = (metafields || []).find(m => m && m.namespace === ns && m.key === key);
    return mf ? mf.value : null;
  }

  function escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Replicates Liquid's money_without_trailing_zeros using the shop's money_format.
  // Handles all four Shopify money format tokens.
  function formatMoney(amount, currencyCode) {
    const n = parseFloat(amount);
    if (isNaN(n)) return '';
    if (moneyFormat) {
      return moneyFormat
        .replace(/\{\{amount_no_decimals_with_comma_separator\}\}/g,
          n.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ','))
        .replace(/\{\{amount_with_comma_separator\}\}/g,
          n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','))
        .replace(/\{\{amount_no_decimals\}\}/g, n.toFixed(0))
        .replace(/\{\{amount\}\}/g, n.toFixed(2))
        .replace(/\.00$/, '')   // strip trailing .00 → money_without_trailing_zeros
        .replace(/,00$/, '');
    }
    // Fallback if moneyFormat is unavailable
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n);
  }

  // ─── Compact Product Converter ────────────────────────────────────────────
  //
  // Converts a raw Storefront API product node into the lean shape used for
  // both rendering and localStorage caching.

  function toCompact(node) {
    const images      = node.images.edges.map(e => e.node);
    const noHover     = getMetafield(node.metafields, 'custom', 'no_hover_image') === 'true';
    const path        = node.onlineStoreUrl
      ? node.onlineStoreUrl.replace(window.location.origin, '')
      : '#';
    const colorOption = node.options.find(o => o.name === 'Color');
    const colors      = colorOption ? colorOption.values : ['Default'];

    const toImgData = img => img
      ? { url: img.url, alt: img.altText || '', w: img.width || 0, h: img.height || 0 }
      : null;

    return {
      title:     node.title,
      path,
      available: node.availableForSale,
      price:     { amount: node.priceRange.minVariantPrice.amount, currency: node.priceRange.minVariantPrice.currencyCode },
      thumbs:    colors.map((color, i) => {
        const rawHover = images[i * 2 + 1] || null;
        return {
          color,
          isDefault: color === 'Default',
          main:  toImgData(images[i * 2] || null),
          hover: (!noHover && rawHover) ? toImgData(rawHover) : null,
        };
      }),
    };
  }

  // ─── HTML Builder ─────────────────────────────────────────────────────────
  //
  // Replicates snippets/product-thumb.liquid exactly.
  // Accepts a compact product object (from cache or freshly converted via toCompact).
  //
  // Returns an array of DOM elements — one per color variant.

  function buildProductThumbs(product) {
    const imgTag = img => img
      ? `<img src="${escapeHtml(img.url)}" alt="${escapeHtml(img.alt)}" loading="lazy" width="${img.w || ''}" height="${img.h || ''}">`
      : '';

    const priceHtml = product.available
      ? formatMoney(product.price.amount, product.price.currency)
      : '<div class="sold-out">Sold Out</div>';

    return product.thumbs.filter(thumb => thumb.main !== null).map(thumb => {
      const query = thumb.isDefault ? '' : `?color=${encodeURIComponent(thumb.color.toLowerCase())}`;

      const html = `<div class="product-thumb loop-post">
  <a class="loop-post-container" href="${escapeHtml(product.path + query)}">
    <div class="thumb-hover-container">${imgTag(thumb.hover)}</div>
    <div class="loop-post-image">${imgTag(thumb.main)}</div>
    <div class="loop-post-info">
      <div class="loop-post-title" data-stock="instock">
        ${escapeHtml(product.title)}${thumb.isDefault ? '' : `<div class="loop-post-color">${escapeHtml(thumb.color)}</div>`}
      </div>
      <div class="loop-post-price"><div class="price">${priceHtml}</div></div>
    </div>
  </a>
</div>`;

      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      return tpl.content.firstElementChild;
    });
  }

  // ─── Render Helper ────────────────────────────────────────────────────────

  function renderProducts(products) {
    const fragment = document.createDocumentFragment();
    for (const product of products) {
      buildProductThumbs(product).forEach(node => fragment.appendChild(node));
    }
    grid.appendChild(fragment);
  }

  // ─── Fetch Helper ────────────────────────────────────────────────────────

  async function gqlFetch(query, variables) {
    const res = await fetch(
      `https://${shopDomain}/api/${API_VERSION}/graphql.json`,
      {
        method: 'POST',
        signal: abortController.signal,
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query, variables }),
      }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json.errors?.length) throw new Error(json.errors.map(e => e.message).join('; '));
    return json.data;
  }

  // ─── Fetch + Render ───────────────────────────────────────────────────────

  async function loadNextBatch() {
    if (isFetching || !hasNext) return;

    isFetching = true;
    errorEl.style.display   = 'none';
    loadingEl.style.display = 'flex';
    abortController = new AbortController();

    try {
      if (cursor === null) {
        // ── Cache check ──────────────────────────────────────────────────────
        // If we have cached products, render them immediately and skip the
        // bootstrap + any API calls for already-fetched pages.
        const cached = readCache();
        if (cached && cached.products.length > 0) {
          renderProducts(cached.products);
          cursor  = cached.cursor;
          hasNext = cached.hasNext;
          if (!hasNext) {
            observer.unobserve(sentinel);
            sentinel.remove();
            return;
          }
          // cursor is now set from cache — skip bootstrap, fall through to fetch
        } else {
          // ── Bootstrap ───────────────────────────────────────────────────────
          // Liquid's paginate cursor is incompatible with the Storefront API.
          // Fetch first 8 (already server-rendered) with a cursor-only query to
          // get a valid endCursor, then proceed.
          const data = await gqlFetch(BOOTSTRAP_QUERY, { handle: collection, first: 8 });
          const col  = data?.collection;
          if (!col) { hasNext = false; observer.unobserve(sentinel); return; }
          cursor  = col.products.pageInfo.endCursor;
          hasNext = col.products.pageInfo.hasNextPage;
          if (!hasNext) { observer.unobserve(sentinel); sentinel.remove(); return; }
        }
      }

      const data = await gqlFetch(QUERY, { handle: collection, first: BATCH_SIZE, after: cursor });
      const col  = data?.collection;
      if (!col) {
        // Collection not found or metafields not exposed — stop silently
        hasNext = false;
        observer.unobserve(sentinel);
        return;
      }

      const { pageInfo, edges } = col.products;
      const compactBatch = [];

      for (const { node } of edges) {
        if (getMetafield(node.metafields, 'custom', 'show_on_archive_page_only') === 'true') continue;
        compactBatch.push(toCompact(node));
      }

      renderProducts(compactBatch);
      appendCache(compactBatch, pageInfo.endCursor, pageInfo.hasNextPage);

      cursor  = pageInfo.endCursor;
      hasNext = pageInfo.hasNextPage;

      if (!hasNext) {
        observer.unobserve(sentinel);
        sentinel.remove();
      }
    } catch (err) {
      if (err.name === 'AbortError') return; // intentional — no error shown
      console.error('[shop-infinite-scroll]', err);
      errorEl.style.display = 'block';
    } finally {
      loadingEl.style.display = 'none';
      isFetching      = false;
      abortController = null;
    }
  }

  // ─── IntersectionObserver ─────────────────────────────────────────────────

  const observer = new IntersectionObserver(
    ([entry]) => { if (entry.isIntersecting) loadNextBatch(); },
    { rootMargin: ROOT_MARGIN }
  );
  observer.observe(sentinel);

  // ─── Retry ────────────────────────────────────────────────────────────────

  retryBtn?.addEventListener('click', () => {
    errorEl.style.display = 'none';
    loadNextBatch();
  });

  // ─── bfcache safety ───────────────────────────────────────────────────────
  // Abort in-flight requests when the page is unloaded or enters the bfcache,
  // preventing duplicate appends if the user navigates back.

  window.addEventListener('pagehide', () => {
    abortController?.abort();
    observer.disconnect();
  });
})();
